import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";

const ruleNames = [
  "Funding goal reached",
  "Supplier verified",
  "Invoice matches budget",
  "Evidence complete",
  "Community approval 70%+",
  "Stage order correct",
  "No duplicate payment",
  "Banking details verified",
];

const rules = [
  { title: "Funding goal has been reached", desc: "Nothing releases until the community has actually funded the milestone." },
  { title: "Supplier is verified", desc: "Payment can only go to a business already checked and confirmed real." },
  { title: "Invoice matches the approved budget", desc: "No payment can exceed or drift from what was originally agreed." },
  { title: "Evidence is complete", desc: "Photos, receipts, or proof of work must be fully submitted first." },
  { title: "Community approval reaches 70% or above", desc: "The people who funded it must agree the milestone is genuinely met." },
  { title: "Project stages are in order", desc: "A project can't skip ahead, every stage happens in sequence." },
  { title: "No duplicate payments", desc: "The same invoice can never be paid out twice." },
  { title: "Banking details match verified records", desc: "Money only reaches the exact account already confirmed as real." },
];

const journeyFlow = [
  "Contribution Received",
  "Placed Under Forge Vault Governance",
  "Locked to Its Project",
  "Funding Goal Reached",
  "Forge Vault Reviews Evidence",
  "Milestone Verified",
  "Payment Authorised",
  "Recorded on the Public Ledger",
];

function VaultAnimation() {
  const [litCount, setLitCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("Waiting for governance check to begin...");
  const [status, setStatus] = useState("");
  const timeouts = useRef([]);

  useEffect(() => {
    const run = () => {
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
      setLitCount(0);
      setOpen(false);
      setStatus("");
      setLabel("Waiting for governance check to begin...");

      ruleNames.forEach((name, i) => {
        const t = setTimeout(() => {
          setLitCount(i + 1);
          setLabel(`Checking: ${name}`);
        }, 600 + i * 500);
        timeouts.current.push(t);
      });

      const finalT = setTimeout(() => {
        setOpen(true);
        setLabel("All 8 rules satisfied.");
        setStatus("Payment Authorised");
      }, 600 + 8 * 500 + 400);
      timeouts.current.push(finalT);
    };

    run();
    const interval = setInterval(run, 600 + 8 * 500 + 400 + 3000);
    return () => {
      clearInterval(interval);
      timeouts.current.forEach(clearTimeout);
    };
  }, []);

  const radius = 100;
  const dots = Array.from({ length: 8 }, (_, i) => {
    const angle = (i / 8) * 2 * Math.PI - Math.PI / 2;
    const x = 110 + radius * Math.cos(angle) - 8;
    const y = 110 + radius * Math.sin(angle) - 8;
    return { x, y, lit: i < litCount };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0 20px" }}>
      <div style={{ position: "relative", width: 220, height: 220 }}>
        {dots.map((d, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: d.x, top: d.y,
              width: 16, height: 16, borderRadius: "50%",
              background: d.lit ? "#C9A84C" : "rgba(184,212,232,0.15)",
              boxShadow: d.lit ? "0 0 12px rgba(201,168,76,0.7)" : "none",
              transition: "background 0.4s, box-shadow 0.4s",
            }}
          />
        ))}
        <div
          style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: 90, height: 90, borderRadius: 20,
            background: open ? "rgba(201,168,76,0.15)" : "rgba(255,255,255,0.06)",
            border: `1.5px solid ${open ? "#C9A84C" : "rgba(184,212,232,0.25)"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.6s ease",
          }}
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/>
            <path d={open ? "M7 11V7a5 5 0 0110 0v2" : "M7 11V7a5 5 0 0110 0v4"} style={{ transition: "all 0.6s ease" }}/>
          </svg>
        </div>
      </div>
      <div style={{ marginTop: 24, fontSize: 13, color: "rgba(184,212,232,0.7)", textAlign: "center", minHeight: 20 }}>
        {label}
      </div>
      <div style={{ marginTop: 8, fontSize: 17, fontWeight: 800, color: "#C9A84C", textAlign: "center", minHeight: 26 }}>
        {status}
      </div>
    </div>
  );
}

export default function ForgeVault() {
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: "var(--font-body)", minHeight: "100vh" }}>
      <Navbar active="Forge Vault" />

      <style>{`
        .fv-wrap { max-width: 720px; margin: 0 auto; padding: 56px 24px 0; }
        .fv-flow-step { display: flex; align-items: center; gap: 14px; padding: 12px 0; border-bottom: 1px solid rgba(184,212,232,0.1); font-size: 14px; color: rgba(184,212,232,0.9); }
        .fv-flow-step:last-child { border-bottom: none; }
        .fv-rule-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(184,212,232,0.12); border-radius: 14px; padding: 18px 20px; margin-bottom: 10px; display: flex; gap: 14px; align-items: flex-start; }
        .fv-cta { padding: 64px 24px; margin-top: 56px; text-align: center; }
        @media (max-width: 768px) {
          .fv-wrap { padding: 40px 20px 0; }
          .fv-cta { padding: 48px 20px; margin-top: 40px; }
          .fv-cta button { width: 100%; }
        }
      `}</style>

      <div style={{ background: "#1B3A6B", color: "#fff", paddingBottom: 56 }}>
        <div className="fv-wrap">
          <div style={{ display: "inline-block", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.35)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#C9A84C", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
            Forge Vault
          </div>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 44px)", fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 12px", lineHeight: 1.1 }}>
            Protecting every contribution <span style={{ color: "#C9A84C" }}>through transparent governance.</span>
          </h1>
          <p style={{ fontSize: 15, color: "rgba(184,212,232,0.85)", lineHeight: 1.7, maxWidth: 480, marginBottom: 32 }}>
            Forge Vault is not a bank, and it does not hold your money. It is the process that decides when money can move, and why.
          </p>

          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(184,212,232,0.15)", borderRadius: 16, padding: 24, fontSize: 15, lineHeight: 1.8, color: "rgba(184,212,232,0.95)", marginBottom: 40 }}>
            <strong style={{ color: "#fff" }}>Forge Vault doesn't protect money by locking it away.</strong> It protects money by ensuring every project follows the same transparent governance process before any funds can be authorised for release.
          </div>

          <VaultAnimation />

          <div style={{ marginTop: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(184,212,232,0.6)", marginBottom: 20 }}>
              The Journey of a Contribution
            </div>
            {journeyFlow.map((step, i) => (
              <div key={i} className="fv-flow-step">
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#5B9BD5", flexShrink: 0 }} />
                {step}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(184,212,232,0.6)", marginBottom: 20 }}>
              The 8 Rules, Explained
            </div>
            {rules.map((rule, i) => (
              <div key={i} className="fv-rule-card">
                <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(201,168,76,0.15)", color: "#C9A84C", fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{rule.title}</div>
                  <div style={{ fontSize: 12, color: "rgba(184,212,232,0.75)", lineHeight: 1.6 }}>{rule.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, color: "rgba(184,212,232,0.5)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 40, textAlign: "center" }}>
            This animation illustrates the process. It does not reflect a real transaction.
          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="fv-cta" style={{ background: "#F5F0E8" }}>
        <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 900, color: "#1A1A2E", margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          See it in action on <span style={{ color: "#1B3A6B" }}>every project.</span>
        </h2>
        <p style={{ color: "#888", fontSize: 14, marginBottom: 24 }}>
          Forge Vault governs every rand, on every project, the same way, every time.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => navigate("/projects")} style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "13px 28px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}>
            Explore Projects
          </button>
          <button onClick={() => navigate("/howitworks")} style={{ background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "13px 28px", color: "#4A4A6A", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}>
            How It Works
          </button>
        </div>
      </section>

    </div>
  );
}